import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectedIssues"]

  connect() {
    this.init()
  }

  init() {
    const selectedIssues = Array.from(this.selectedIssuesTarget.querySelectorAll("div[data-issue-id]"))

    selectedIssues.forEach(selectedIssue => {
      const issueId = selectedIssue.dataset.issueId
      const issueLi = document.querySelector(`li[data-issue-id="${issueId}"]`)

      if (issueLi) {
        this.markAsSelected(issueLi)
      }
    })

    this.togglePriorityOrderHeading();

    if (selectedIssues.length > 0) {
      this.showPriorityList();
      this.hideDescriptionInput();
    }
  }

  hideDescriptionInput() {
    const descriptionInput = document.querySelector('[data-priorities-toggle-target="descriptionInput"]')
    if (descriptionInput) descriptionInput.classList.add("hidden")
  }

  showPriorityList() {
    const priorityList = document.querySelector('[data-priorities-toggle-target="priorityList"]')
    if (priorityList) priorityList.classList.remove("hidden")
  }

  toggleIssue(event) {
    const parentLi = event.target.closest('li')
    const issueId = parentLi.dataset.issueId
    const issueName = parentLi.dataset.issueName
    const squareCheckIcon = parentLi.querySelector(".square-check-icon")
    const isSelected = !squareCheckIcon.classList.contains("hidden")

    if (isSelected) {
      this.unmarkAsSelected(parentLi)
      this.removeIssue(issueId)
    } else {
      this.markAsSelected(parentLi)
      this.addIssue(issueId, issueName)
    }

    this.togglePriorityOrderHeading()
  }

  markAsSelected(element) {
    const squareIcon = element.querySelector(".square-icon")
    const squareCheckIcon = element.querySelector(".square-check-icon")

    squareIcon.classList.add("hidden")
    element.classList.add("scale-105", "bg-gray-900", "border-green-300")
    squareCheckIcon.classList.remove("hidden")
  }

  unmarkAsSelected(element) {
    const squareIcon = element.querySelector(".square-icon")
    const squareCheckIcon = element.querySelector(".square-check-icon")

    squareIcon.classList.remove("hidden")
    element.classList.remove("scale-105", "bg-gray-900", "border-green-300")
    squareCheckIcon.classList.add("hidden")
  }

  addIssue(issueId, issueName) {
    const existingElement = this.selectedIssuesTarget.querySelector(`[data-issue-id="${issueId}"]`);

    if (existingElement) {
      const destroyField = existingElement.querySelector(`input[name="survey[issue_orders_attributes][][_destroy]"]`);

      if (destroyField && destroyField.value === "1") {
        destroyField.value = "0";
        existingElement.classList.remove("hidden");
      }

      return;
    }

    const inputElement = document.createElement("input")
    inputElement.type = "hidden"
    inputElement.name = "survey[issue_orders_attributes][][issue_id]"
    inputElement.value = issueId
    inputElement.setAttribute("data-issue-id", issueId)

    const destroyField = document.createElement("input")
    destroyField.type = "hidden"
    destroyField.name = "survey[issue_orders_attributes][][_destroy]"
    destroyField.value = "0"
    destroyField.setAttribute("data-destroy-field", true)

    // Create the main div element
    const displayElement = document.createElement("div");
    displayElement.classList.add(
      "flex", "items-center", "gap-2", "my-2", "cursor-pointer", "sortable-row"
    );
    displayElement.setAttribute("data-issue-id", issueId);

    displayElement.innerHTML = `
      <div class="flex items-center gap-3">
        <button type="button" data-action="click->move-issues#moveUp" class="cursor-pointer text-blue-400 hover:text-purple-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-chevron-up-icon lucide-square-chevron-up"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="m8 14 4-4 4 4"/></svg>
        </button>
        <button type="button" data-action="click->move-issues#moveDown" class="cursor-pointer text-blue-400 hover:text-purple-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-chevron-down-icon lucide-square-chevron-down"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="m16 10-4 4-4-4"/></svg>
        </button>
      </div>

      <div class="flex justify-between w-full px-6 py-3 bg-white rounded-xl shadow-md hover:shadow-lg">
        <strong class="flex-grow text-start text-gray-700 ms-2">${issueName}</strong>
        <button type="button" data-action="click->issue-selection#removeIssue" class="text-red-500 cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x-icon lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </div>
    `;

    displayElement.appendChild(inputElement);
    displayElement.appendChild(destroyField);

    this.selectedIssuesTarget.appendChild(displayElement)
  }

  removeIssue(eventOrIssueId) {
    let issueId;

    if (typeof eventOrIssueId === 'string') {
      issueId = eventOrIssueId;
    } else {
      const parentLi = eventOrIssueId.target.closest('[data-issue-id]');
      issueId = parentLi.dataset.issueId;
    }

    // Show a confirmation dialog before deleting
    if (!confirm("Are you sure you want to delete this issue?")) {
        return; // Exit the function if the user cancels the confirmation
    }

    const issueElement = this.selectedIssuesTarget.querySelector(`[data-issue-id="${issueId}"]`);
    const destroyField = issueElement.querySelector(`input[name="survey[issue_orders_attributes][][_destroy]"]`);

    if (destroyField) {
      destroyField.value = "1";
    } else {
      const newDestroyField = document.createElement("input");
      newDestroyField.type = "hidden";
      newDestroyField.name = "survey[issue_orders_attributes][][_destroy]";
      newDestroyField.value = "1";
      issueElement.appendChild(newDestroyField);
    }

    const issueOptions = document.querySelectorAll(`.issue-option-${issueId}`);
    issueOptions.forEach(option => this.unmarkAsSelected(option));

    issueElement.classList.add("hidden");
  }

  togglePriorityOrderHeading() {
    const heading = document.getElementById("priority-order-heading")
    const hasIssues = this.selectedIssuesTarget.querySelectorAll('div[data-issue-id]').length > 0

    if (hasIssues) {
      heading.classList.remove("hidden")
    } else {
      heading.classList.add("hidden")
    }
  }
}
